// @flow

import Promise from 'bluebird';
import pixelPromise from 'src/modules/pixel-promise';
import createPagination from 'giant-piano';

import { hashToUserQueryId } from '@seek/seek-jobs-analytics';
import { searchResultsViewModel } from '@seek/seek-refine-job-search';
import {
  classificationById,
  subClassificationById
} from '@seek/seek-classifications-hierarchy';
import { normaliseSearchQuery } from '@seek/seek-jobs-seo';
import createApiClient from 'src/modules/seek-jobs-api-client';
import { isTimeoutError } from 'src/modules/seek-api-request';
import { uniq, get, includes } from 'lodash';
import { LOCATION_CHANGED, setPageTitle } from 'src/store/location/location';
import { updateIsSavedSearch } from 'src/store/saveSearch/saveSearch';
import { CLEAR_NUDGE_ERROR } from 'src/store/nudges/nudges';
import { SUBMIT_SEARCH } from 'src/store/search/search';
import { getDecodedTokenWithoutIndex } from './results.token';
import VError from 'verror';

const api = createApiClient();

const paginate = createPagination({ itemsPerPage: 20, maxPages: 7 });

export const MORE_OPTIONS = 'MORE_OPTIONS';
export const GET_RESULTS_BEGIN = 'GET_RESULTS_BEGIN';
export const CLEAR_RESULTS = 'CLEAR_RESULTS';
export const GET_RESULTS_SUCCESS = 'GET_RESULTS_SUCCESS';
const GET_RESULTS_ERROR = 'GET_RESULTS_ERROR';
const GET_RESULTS_ERROR_CANCELLATION = 'GET_RESULTS_ERROR_CANCELLATION';
const FETCH_SEARCH_RESULTS_ERROR = 'FETCH_SEARCH_RESULTS_ERROR';
export const RESULTS_PAGE_LOAD = 'RESULTS_PAGE_LOAD';
const RESULT_LINK_CLICKED = 'RESULT_LINK_CLICKED';
const JORA_RESULT_LINK_CLICKED = 'JORA_RESULT_LINK_CLICKED';
const RELATED_SEARCH_CLICKED = 'RELATED_SEARCH_CLICKED';
const EMPTY_RELATED_SEARCH = [];

const initialState = {
  results: null,
  isLoading: false,
  isError: false,
  source: '',
  canonicalCompany: null,
  companySuggestion: null,
  location: null,
  locationWhere: null,
  title: '',
  totalCount: null,
  jobIds: [],
  pagination: null,
  paginationParameters: {},
  sortMode: null,
  hidden: false,
  relatedSearches: EMPTY_RELATED_SEARCH
};

const calculateResults = ({
  query = {},
  results = null,
  location,
  totalCount,
  suggestions
}: Object = {}) => {
  const page = query.page || '1';

  const viewModel = searchResultsViewModel({
    page,
    results,
    location,
    totalCount,
    suggestions,
    searchParams: query
  });

  const jobIds = results ? results.map(job => job.id) : [];

  return {
    results: {
      ...viewModel,
      suburbs: results
        ? uniq(results.map(job => job.suburbWhereValue).filter(Boolean))
        : []
    },
    jobIds
  };
};

export default function reducer(state: * = initialState, action: * = {}) {
  switch (action.type) {
    case MORE_OPTIONS: {
      return {
        ...state,
        hidden: true
      };
    }

    case CLEAR_RESULTS:
    case GET_RESULTS_BEGIN: {
      const { query = {} } = action.payload || {};

      return {
        ...state,
        ...calculateResults({ query }),
        source: '',
        isLoading: true,
        isError: false,
        companySuggestion: null,
        canonicalCompany: null,
        location: {},
        relatedSearches: EMPTY_RELATED_SEARCH,
        hidden: false
      };
    }

    case SUBMIT_SEARCH: {
      return {
        ...state,
        isLoading: true
      };
    }

    case LOCATION_CHANGED: {
      const { pathname } = action.payload.location;
      const isHomepage = pathname === '/';
      const title = isHomepage ? '' : state.title; // Use default localised title for home page
      return {
        ...state,
        title
      };
    }

    case GET_RESULTS_SUCCESS: {
      const {
        query,
        currentPageNumber,
        userQueryId,
        relatedSearches
      } = action.payload;

      const {
        title,
        data,
        totalCount,
        location = null,
        suggestions,
        paginationParameters = {},
        sortMode = null,
        canonicalCompany,
        companySuggestion,
        joraCrossLink,
        info,
        solMetadata
      } = action.payload.result;

      const locationWhere =
        location === null ||
        location.description === 'All Australia' ||
        location.description === 'All New Zealand'
          ? ''
          : location.description;
      const pagination =
        totalCount === 0
          ? null
          : paginate({
              currentPage: currentPageNumber,
              totalItems: totalCount
            });
      const tracking = get(action.payload, 'result.data[0].tracking');
      const uniqueSearchToken = getDecodedTokenWithoutIndex(tracking);
      const { source } = info || {};
      const solMetadataString = JSON.stringify(solMetadata || {});

      return {
        ...state,
        ...calculateResults({
          query,
          results: data,
          location,
          totalCount,
          suggestions
        }),
        isLoading: false,
        isError: false,
        source,
        companySuggestion,
        canonicalCompany,
        joraCrossLink,
        location,
        locationWhere,
        title,
        totalCount,
        userQueryId,
        pagination,
        paginationParameters,
        sortMode,
        relatedSearches,
        uniqueSearchToken,
        solMetadata,
        solMetadataString,
        requiresSolMonitor: !ENV.SERVER
      };
    }

    case GET_RESULTS_ERROR: {
      return {
        ...state,
        ...calculateResults(),
        source: '',
        isLoading: false,
        isError: true,
        title: 'Error loading results',
        pagination: null,
        relatedSearches: EMPTY_RELATED_SEARCH
      };
    }

    default: {
      return state;
    }
  }
}

export const resultPageLoad = () => {
  return {
    type: RESULTS_PAGE_LOAD
  };
};

export const moreOptions = () => ({
  type: MORE_OPTIONS,
  meta: {
    hotjar: 'More Options Clicked'
  }
});

export const getRelatedSearchTerm = ({
  companyname,
  keywords,
  classification,
  subclassification
}: *) => {
  const getFirstNameFromSortedList = (str = '', fn) =>
    fn(str.split(',').sort()[0]);
  const lower = (str = '') => str.toLowerCase();

  return (
    lower(keywords) ||
    lower(companyname) ||
    lower(
      getFirstNameFromSortedList(subclassification, subClassificationById)
    ) ||
    lower(getFirstNameFromSortedList(classification, classificationById)) ||
    false
  );
};

const fetchSearchResults = ({
  apiQuery = {},
  locale,
  cookies,
  userQueryId,
  requestId,
  tries = 2,
  timeout,
  userAgent
}: Object) => (dispatch: Function) => {
  return api.jobs
    .search({
      searchParams: apiQuery,
      locale,
      cookies,
      userQueryId,
      requestId,
      timeout,
      userAgent
    })
    .catch(err => {
      dispatch({
        type: FETCH_SEARCH_RESULTS_ERROR,
        meta: {
          metrics: {
            name: FETCH_SEARCH_RESULTS_ERROR
          },
          prestoLogging: {
            data: { userQueryId }
          }
        }
      });

      const retryUserQueryId = hashToUserQueryId({
        query: apiQuery.normalisedQuery
      });

      if (get(err, 'response.status') === 400) {
        return {
          totalCount: 0,
          data: []
        };
      }

      if (isTimeoutError(err) || get(err, 'response.status') >= 500) {
        if (tries === 1) {
          throw err;
        }

        return fetchSearchResults({
          apiQuery,
          locale,
          cookies,
          userQueryId: retryUserQueryId,
          requestId,
          tries: tries - 1
        })(dispatch);
      }

      throw err;
    });
};

const fetchRelatedSearches = (country, relatedSearchTerm, requestId) => {
  if (!relatedSearchTerm) {
    return Promise.resolve(EMPTY_RELATED_SEARCH);
  }
  return api.jobs
    .relatedSearches({
      country: country.toLowerCase(),
      keywords: relatedSearchTerm,
      requestId,
      timeout: 250
    })
    .then(result => (result ? result.relatedSearches : EMPTY_RELATED_SEARCH))
    .catch(() => {
      // ignore error because dont want to block search
    });
};

export const getResults = (
  {
    locale,
    path,
    query,
    paginationParameters = {},
    cookies,
    requestId,
    userAgent
  }: *,
  referrer: *
) => (dispatch: Function, getState: Function) => {
  const normalisedQuery = normaliseSearchQuery({ path, query });
  const apiQuery = { ...normalisedQuery, ...paginationParameters };
  const userQueryId = hashToUserQueryId({ query: normalisedQuery });
  const relatedSearchTerm = getRelatedSearchTerm(apiQuery);

  dispatch({ type: CLEAR_NUDGE_ERROR });
  dispatch(updateIsSavedSearch(apiQuery));

  dispatch({
    type: GET_RESULTS_BEGIN,
    payload: {
      query: normalisedQuery,
      prestoLogging: {
        data: {
          userQueryId
        }
      },
      metrics: {
        name: GET_RESULTS_BEGIN
      }
    }
  });

  const fetchData = Promise.all([
    fetchSearchResults({
      apiQuery,
      locale,
      cookies,
      userQueryId,
      requestId,
      userAgent
    })(dispatch),
    fetchRelatedSearches(locale, relatedSearchTerm, requestId)
  ]);

  return fetchData
    .then(([result, relatedSearches]) => {
      const currentPageNumber = getState().location.pageNumber;
      const totalCount = get(result, 'totalCount', 0);
      const zeroResults = totalCount === 0;
      const hotjarTags = zeroResults ? ['Zero Results'] : [];
      const referrerTag = includes(referrer, 'google')
        ? { referrer: 'google' }
        : {};

      dispatch({
        type: GET_RESULTS_SUCCESS,
        payload: {
          query: normalisedQuery,
          result,
          currentPageNumber,
          userQueryId,
          relatedSearches
        },
        meta: {
          prestoLogging: {
            data: {
              zeroResults,
              userQueryId,
              actionType: GET_RESULTS_SUCCESS
            }
          },
          metrics: {
            name: GET_RESULTS_SUCCESS,
            tags: {
              zeroResults,
              companySearch: Boolean(get(result, 'companySuggestion')),
              keywordSearch: Boolean(normalisedQuery.keywords),
              ...referrerTag
            }
          },
          hotjar: hotjarTags
        }
      });

      const { title } = getState().results;

      dispatch(setPageTitle(title));
    })
    .catch(Promise.CancellationError, err => {
      dispatch({
        type: GET_RESULTS_ERROR_CANCELLATION,
        error: true,
        meta: {
          metrics: {
            name: GET_RESULTS_ERROR_CANCELLATION
          },
          prestoLogging: {
            data: {
              userQueryId
            }
          } // Adds the middleware behaviour of explicitly logging this Redux action along with a unique search token
        }
      });
      throw new VError(err, 'fetchData promise was cancelled');
    })
    .catch(err => {
      dispatch({
        type: GET_RESULTS_ERROR,
        error: true,
        payload: err,
        meta: {
          metrics: {
            name: GET_RESULTS_ERROR
          },
          prestoLogging: {
            data: {
              userQueryId,
              err: err.message
            }
          } // Adds the middleware behaviour of explicitly logging this Redux action along with a unique search token
        }
      });
      throw new VError(err, 'Failed to fetch search data');
    });
};

export const resultLinkClicked = (job: *) => (dispatch: Function) => {
  const { joraClickTrackingUrl } = job;

  dispatch({
    type: RESULT_LINK_CLICKED,
    meta: {
      metrics: {
        name: RESULT_LINK_CLICKED
      }
    }
  });

  if (joraClickTrackingUrl) {
    dispatch({
      type: JORA_RESULT_LINK_CLICKED,
      meta: {
        metrics: {
          name: JORA_RESULT_LINK_CLICKED
        }
      }
    });

    return pixelPromise(joraClickTrackingUrl);
  }
  return null;
};

export const relatedSearchClicked = () => ({
  type: RELATED_SEARCH_CLICKED,
  meta: {
    hotjar: 'Related search clicked',
    metrics: {
      name: RELATED_SEARCH_CLICKED
    }
  }
});

export const clearResults = () => ({
  type: CLEAR_RESULTS
});

export const jobsSelector = (results: *) => {
  const {
    results: { jobs, tiers }
  } = results || {};

  if (jobs !== null) {
    // jobs is null when we have tiered results
    return jobs;
  }

  return Array.prototype.concat.apply([], tiers.map(tier => tier.jobs));
};

export { fetchSearchResults as fetchSearchResultsForTest };
